import { useEffect, useState } from 'react'
import { useScript } from 'usehooks-ts'
import { renderToStaticMarkup } from 'react-dom/server'
import { documentToReactComponents, type Options } from '@contentful/rich-text-react-renderer'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import RichText from '@/components/ui/rich-text.tsx'
import type { TypeComponentHubSpotForm } from '@/types/ctf'

import { usePage } from '@inertiajs/react'
import { defaultLocale, isBrowserInEuRegion, localizedPath, useLocale, useTranslations } from '@/utils/locale'
import { cx } from '@/utils/strings'
import ShieldLoader from '@/components/ui/shield-loader'
import DOMPurify from 'isomorphic-dompurify'

// Minimal renderer that resolves Contentful entry- and asset-hyperlinks to
// plain anchor tags. The default `documentToReactComponents` doesn't handle
// those node types and falls back to literal "type: entry-hyperlink id: …".
// Paragraphs render as fragments so the output stays inline-safe when injected
// into a `<span>` (e.g. a HubSpot checkbox label).
const richTextOptions = (currentLocale: string): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node, children) => <>{children}</>,
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const target = (node as any)?.data?.target?.fields
      const raw = target?.url || target?.slug || '#'
      return (
        <a href={localizedPath(raw, currentLocale) || raw} className="underline">
          {children}
        </a>
      )
    },
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      const url = (node as any)?.data?.target?.fields?.file?.url
      return (
        <a href={url || '#'} className="underline">
          {children}
        </a>
      )
    },
    [INLINES.HYPERLINK]: (node, children) => {
      const uri = (node as any)?.data?.uri || '#'
      return (
        <a href={uri} className="underline">
          {children}
        </a>
      )
    },
  },
})

// Translation entries store the phrase in either `phrase` (plain text) or
// `phraseRichText` (Contentful rich-text JSON). Read whichever is populated
// and return the appropriate representation.
const getPhraseText = (t: any): string | null => {
  if (t?.fields?.phrase) return t.fields.phrase as string
  if (t?.fields?.phraseRichText) return documentToPlainTextString(t.fields.phraseRichText)
  return null
}
const getPhraseHtml = (t: any, currentLocale: string): string | null => {
  if (t?.fields?.phraseRichText) {
    return renderToStaticMarkup(
      documentToReactComponents(t.fields.phraseRichText, richTextOptions(currentLocale)) as any
    )
  }
  if (t?.fields?.phrase) return t.fields.phrase as string
  return null
}

type HubspotFormFields = TypeComponentHubSpotForm<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']
export type HubspotFormProps = Omit<HubspotFormFields, 'formSize'> & {
  formSize?: HubspotFormFields['formSize'] | 'Xsmall'
  shieldLoader?: 'Xsmall' | 'Small' | 'Medium' | 'Large'
  formHeight?: string
  className?: string
  defaultValues?: Record<string, string>
  onSubmit?: () => void
}

export default function HubspotForm({
  formId,
  headline,
  submitText,
  afterSubmitText,
  afterSubmitLink,
  translations,
  subtext,
  plausibleEventName,
  customErrorMessage,
  formSize,
  formHeight,
  postFormText,
  shieldLoader,
  customListIcon,
  defaultValues,
  className,
  onSubmit,
}: HubspotFormProps) {
  const { url } = usePage()
  const { currentLocale } = useLocale(url)

  const [loadingError, setLoadingError] = useState(false)
  const [targetId, setTargetId] = useState<string | null>(null)
  const [formLoaded, setFormLoaded] = useState(false)
  const [shieldVisible, setShieldVisible] = useState(true)
  const scriptStatus = useScript(`//js.hsforms.net/forms/embed/v2.js`, {
    removeOnUnmount: false,
  })

  const isMITFormPage = url.includes('/go/start')
  const { t } = useTranslations()

  useEffect(() => {
    setTargetId(formId ? `hsf-${formId}-${Math.floor(Math.random() * 1000)}` : null)
  }, [])

  useEffect(() => {
    if (scriptStatus === 'ready' && targetId) {
      setLoadingError(false)
      // Translation keys are dispatched three ways depending on their prefix:
      //   '#...'  → DOM element ID; innerText replaced in onFormReady
      //   '....'  → CSS selector; innerHTML (sanitized) replaced in onFormReady
      //   other   → HubSpot fieldLabels (matched by field internal name)
      const isDefaultLocale = currentLocale === defaultLocale
      // On the default (English) locale, HubSpot's own configured labels and
      // links are the source of truth, so we skip every Contentful override and
      // only translate non-default locales.
      const localeTranslations = isDefaultLocale ? undefined : translations
      const translationMap =
        localeTranslations
          ?.filter((t) => {
            const key = t?.fields?.key
            return key && !key.startsWith('#') && !key.startsWith('.')
          })
          .reduce(
            (acc, curr) => {
              const key = curr?.fields?.key
              const text = getPhraseText(curr)
              if (key && text) acc[key] = text
              return acc
            },
            {} as Record<string, string>
          ) ?? {}

      window.hbspt.forms.create({
        region: 'na1',
        portalId: '22371289',
        formId: `${formId}`,
        pageId: formId,
        target: `#${targetId}`,
        locale: currentLocale,
        cssClass: plausibleEventName,
        ...(afterSubmitText && { inlineMessage: afterSubmitText }),
        ...(afterSubmitLink?.fields.url &&
          !afterSubmitText && { redirectUrl: localizedPath(afterSubmitLink.fields.url, currentLocale) }),
        translations: {
          [currentLocale]: {
            submitText,
            fieldLabels: translationMap,
          },
        },
        onFormReady: (form: HTMLFormElement) => {
          localeTranslations
            ?.filter((t) => t?.fields?.key?.startsWith('#'))
            .forEach((t) => {
              const key = t?.fields?.key
              if (!key) return
              const el = document.getElementById(key.substring(1))
              const text = getPhraseText(t)
              if (el && text) el.innerText = DOMPurify.sanitize(text)
            })

          // CSS-selector-keyed translations. Runs before the tooltip wrap below
          // so the wrap picks up the translated content. innerHTML (sanitized)
          // preserves anchor tags in phrases like "...See more." links.
          localeTranslations
            ?.filter((t) => t?.fields?.key?.startsWith('.'))
            .forEach((t) => {
              const key = t?.fields?.key
              if (!key) return
              const html = getPhraseHtml(t, currentLocale)
              if (!html) return
              const el = document.querySelector(key)
              if (el) el.innerHTML = DOMPurify.sanitize(html)
            })

          document.querySelectorAll('.hs-form-checkbox-display span').forEach((span) => {
            const original = span.textContent?.trim()
            if (original && translationMap[original]) {
              span.textContent = translationMap[original]
            }
          })

          // Preselect the EU data region for users physically in an EU/EEA country
          // (based on their browser locale), independent of which site locale they're viewing.
          if (isBrowserInEuRegion(navigator.language)) {
            const euRadio = document.querySelector<HTMLInputElement>(
              '.hs_trial_initiation_server_selected input[type="radio"][value="EU"]'
            )
            if (euRadio && !euRadio.checked) {
              euRadio.checked = true
              euRadio.dispatchEvent(new Event('change', { bubbles: true }))
            }
          }

          const serverSelectDescription = document.querySelector('.hs_trial_initiation_server_selected .hs-field-desc')
          const labelElement = serverSelectDescription?.parentElement?.querySelector('label')

          // Only add tooltip if it hasn't been added already (prevent double execution)
          if (serverSelectDescription && !serverSelectDescription.querySelector('.bw-mit-info-icon')) {
            serverSelectDescription.classList.add('active')
            serverSelectDescription.innerHTML = `<span class="bw-hs-toolip-content">${DOMPurify.sanitize(serverSelectDescription.innerHTML)}</span>`
            serverSelectDescription.insertAdjacentHTML(
              'afterbegin',
              '<span class="bwi bwi-info text-xl bw-mit-info-icon" />'
            )

            if (labelElement) {
              labelElement.insertAdjacentHTML('beforeend', serverSelectDescription.outerHTML)
            }
            // Remove the original serverSelectDescription element
            serverSelectDescription.remove()
          }

          // Set default values for form fields if provided
          if (defaultValues) {
            Object.entries(defaultValues).forEach(([name, value]) => {
              const field = form.querySelector(`[name="${CSS.escape(name)}"]`)
              if (
                field instanceof HTMLInputElement ||
                field instanceof HTMLTextAreaElement ||
                field instanceof HTMLSelectElement
              ) {
                field.value = value
              }
            })
          }

          setFormLoaded(true)
        },
        onFormSubmit: () => {
          if (onSubmit) {
            onSubmit()
          }
        },
      })
    }
    if (scriptStatus === 'error') {
      setLoadingError(true)
    }
  }, [targetId, scriptStatus])

  return (
    <div
      className={cx('hubspot-form relative', isMITFormPage && 'min-h-[375px] lg:min-h-[560px]', className)}
      data-targetid={targetId}
      data-formid={formId}
    >
      <span className="hubspot-form-copy">
        {headline && <h3 className="hubspot-form-headline">{headline}</h3>}
        {subtext && (
          <div className={cx(isMITFormPage && '[&_ul]:text-base [&_ul>li>p]:text-base! [&_ul>li>p]:mb-2!')}>
            <RichText customListIcon={customListIcon} body={subtext} />
          </div>
        )}
      </span>

      {loadingError && (
        <div className="border bg-secondary-100 dark:bg-secondary-100 text-black dark:text-white px-4 py-2 rounded [&_p]:text-base [&_p]:mb-0 [&_a]:underline [&_a]:text-primaryBlue!">
          {customErrorMessage ? (
            <RichText body={customErrorMessage} />
          ) : (
            <>
              {t('Your browser has blocked this form from loading - check your browser settings.')}{' '}
              {isMITFormPage ? (
                <a href="https://vault.bitwarden.com/#/signup">{t('Get started')}</a>
              ) : (
                <a href={localizedPath('/contact/', currentLocale)}>{t('Contact support')}</a>
              )}
            </>
          )}
        </div>
      )}

      <div
        className={cx('relative', isMITFormPage && 'min-h-[400px]')}
        style={!isMITFormPage && formHeight ? { minHeight: `${formHeight}px` } : undefined}
      >
        {!loadingError && shieldVisible && (
          <ShieldLoader
            containerHeight={isMITFormPage ? 340 : formHeight ? parseInt(formHeight) : 0}
            shieldSize={isMITFormPage ? 'Medium' : (shieldLoader ?? formSize)}
            className={cx(
              'absolute inset-0 transition-opacity duration-250',
              !formLoaded && 'animate-fade-in-delayed',
              formLoaded && 'opacity-0 pointer-events-none'
            )}
            onTransitionEnd={() => formLoaded && setShieldVisible(false)}
          />
        )}
        <div
          className={cx('hubspot-form-container transition-opacity duration-700', !formLoaded && 'opacity-0')}
          id={targetId || undefined}
        ></div>
        {postFormText && (
          <div
            className={cx(
              'post-form-text min-h-[20px] hubspot-form-post-tex [&_p]:text-sm [&_p]:text-pretty pt-8 transition-opacity duration-700 ',
              !formLoaded && 'opacity-0'
            )}
          >
            <RichText body={postFormText} />
          </div>
        )}
      </div>
    </div>
  )
}
